"use client";

import {ArrowDown} from "lucide-react";
import * as React from "react";

import {Button} from "@components/ui/button";
import {useAutoScroll} from "@components/ui/chat/hooks/useAutoScroll";

interface ChatMessageListProps extends React.HTMLAttributes<HTMLDivElement> {
    smooth?: boolean;
}

const ChatMessageList = React.forwardRef<HTMLDivElement, ChatMessageListProps>(
    ({className, children, smooth = false, ...props}, ref) => {
        const {
            scrollRef,
            isAtBottom,
            scrollToBottom,
            disableAutoScroll,
        } = useAutoScroll({
            smooth,
            content: children,
        });

        // Combine the forwarded ref with the internal scrollRef
        const combinedRef = React.useCallback(
            (node: HTMLDivElement | null) => {
                if (scrollRef) {
                    scrollRef.current = node;
                }
                if (typeof ref === 'function') {
                    ref(node);
                } else if (ref) {
                    ref.current = node;
                }
            },
            [ref, scrollRef]
        );

        return (
            <div className="relative w-full h-full">
                <div
                    className={`flex flex-col w-full h-full p-4 overflow-y-auto rounded-lg bg-card border-1 border-gray-50 ${className}`}
                    ref={combinedRef}
                    onWheel={disableAutoScroll}
                    onTouchMove={disableAutoScroll}
                    {...props}
                >
                    <div className="flex flex-col gap-6">
                        {children}
                    </div>
                </div>

                {!isAtBottom && (
                    <Button
                        onClick={() => {
                            scrollToBottom();
                        }}
                        size="icon"
                        variant="outline"
                        className="absolute bottom-2 left-1/2 transform -translate-x-1/2 inline-flex rounded-full shadow-md"
                        aria-label="Scroll to bottom"
                    >
                        <ArrowDown className="h-4 w-4"/>
                    </Button>
                )}
            </div>
        );
    }
);

ChatMessageList.displayName = "ChatMessageList";

export {ChatMessageList};
